import { associationFor } from '@/config/fileAssociations';
import { getApp } from '@/apps/registry';
import type { FsNode, SpecialFolder } from './types';

const FOLDER_ICONS: Record<SpecialFolder, string> = {
  drive: '/assets/icons/drive.svg',
  'my-computer': '/assets/icons/my-computer.svg',
  'my-documents': '/assets/icons/my-documents.svg',
  'my-pictures': '/assets/icons/my-pictures.svg',
  desktop: '/assets/icons/folder.svg',
  'program-files': '/assets/icons/folder.svg',
  'control-panel': '/assets/icons/control-panel.svg',
  'recycle-bin': '/assets/icons/recycle-bin.svg',
};

export const GENERIC_FILE_ICON = '/assets/icons/file.svg';
export const FOLDER_ICON = '/assets/icons/folder.svg';

/** Best icon for a node: explicit override → shortcut target → extension → generic. */
export function iconFor(node: FsNode): string {
  if (node.icon) return node.icon;

  if (node.type === 'folder') {
    return node.special ? (FOLDER_ICONS[node.special] ?? FOLDER_ICON) : FOLDER_ICON;
  }

  // A shortcut wears the icon of whatever it points at.
  if (node.data.kind === 'app') {
    const app = getApp(node.data.appId);
    if (app) return app.icon;
  }
  if (node.data.kind === 'url') return '/assets/icons/url.svg';

  return associationFor(node.ext)?.icon ?? GENERIC_FILE_ICON;
}

/**
 * What Explorer called a special folder in its Type column. Everything else
 * is a File Folder — including My Documents, which despite the icon was one.
 */
const FOLDER_TYPES: Partial<Record<SpecialFolder, string>> = {
  drive: 'Local Disk',
  'my-computer': 'System Folder',
  'control-panel': 'System Folder',
  'recycle-bin': 'System Folder',
};

/** Explorer's "Type" column. */
export function typeNameFor(node: FsNode): string {
  if (node.type === 'folder') {
    return (node.special && FOLDER_TYPES[node.special]) || 'File Folder';
  }
  if (node.data.kind === 'app') {
    const app = getApp(node.data.appId);
    return app ? `Shortcut to ${app.name}` : 'Shortcut';
  }
  return associationFor(node.ext)?.typeName ?? 'File';
}

/** Shortcut extensions XP hid, even with "hide known file types" turned off. */
const HIDDEN_EXTS = new Set(['.lnk', '.url']);

/**
 * What a node is *called* on screen. The desktop, Explorer, the delete
 * confirmation and the properties box all have to agree, or a file's label and
 * the dialog asking about it would name two different things.
 */
export function displayName(node: FsNode): string {
  if (node.type === 'file' && HIDDEN_EXTS.has(node.ext)) {
    return node.name.slice(0, node.name.length - node.ext.length);
  }
  return node.name;
}
